/**
 * AI obstacle avoidance (spec §7): in every state the pilot casts a ray along its velocity, `avoidLookahead`
 * seconds ahead, and on a hit adds a steer-away term to its pitch and yaw commands. Allocation-free (D-014).
 *
 * A ship has width, so one centre ray misses a rock the hull would still clip. When the centre ray is
 * clear, four parallel "feeler" rays offset `FEELER_RADII` ship radii to the sides of the flight path are
 * cast until one hits: at most five casts per pilot per tick. `feeler` picks which side goes first, so
 * pilots spread their first casts (and the choice between two hits) deterministically.
 */
import { clamp, inverseRotateVec, rotateVec, type Quat, type Vec3 } from '../core/math'
import type { AiTuning } from '../data/schemas'
import { createRay, createRayHit, setRay, type RaycastFn } from '../physics/physics'
import { Control, Pos64, Vel } from '../world/components'
import { loadRot } from './steering'

/** Below this speed (m/s) the ray follows the nose instead of the velocity. */
const MIN_SPEED = 1
/** Feeler offset from the flight path, in ship collider radii (the clearance a pilot keeps from rock). */
export const FEELER_RADII = 4

// Module scratch (D-014).
const hit = createRayHit()
const ray = createRay()
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const v: Vec3 = { x: 0, y: 0, z: 0 }
const n: Vec3 = { x: 0, y: 0, z: 0 }

/**
 * `avoidObstacles`' arguments and result. `avoidObstacles` is over V8's inlining limit, and a float passed to or
 * returned from a call that is not inlined is boxed per call, so hot callers pass these through a scratch of their
 * own. A class, not a literal, so its float fields stay unboxed (gate-A decision).
 */
export class AvoidArgs {
  /** The pilot's own target, whose hits are ignored (-1 = none). */
  ignoreEid = -1
  /** The ship's collider radius (m). */
  radius = 0
  /** Any integer: picks the first feeler side. */
  feeler = 0
  /** Result: the urgency (0 = clear). */
  urgency = 0
}

const avoidScratch = new AvoidArgs()

/**
 * `avoidObstacles` with the arguments as numbers; returns the urgency. Small enough for V8 to inline; a hot caller
 * that is not inlined keeps its own `AvoidArgs` and calls `avoidObstacles`.
 */
export function applyAvoidance(eid: number, raycast: RaycastFn, tuning: AiTuning, ignoreEid: number, radius: number, feeler: number): number {
  const a = avoidScratch
  a.ignoreEid = ignoreEid
  a.radius = radius
  a.feeler = feeler
  avoidObstacles(eid, raycast, tuning, a)
  return a.urgency
}

/**
 * Casts ship `eid`'s avoidance rays and, on a hit, steers away: `avoidGain × urgency` is added to
 * `Control.pitch` and `yaw` toward the side the hit surface faces (the surface normal's component across
 * the nose; a head-on hit pulls up), while the pilot's own turn fades by `1 − urgency` so pursuit cannot
 * cancel it. It also strafes away (sway/heave) and cancels boost. `urgency` = 1 − distance ÷ ray length.
 * Hits on `a.ignoreEid` (the pilot's own target, which anti-joust and range holding already manage) are
 * ignored. `a.radius` is the ship's collider radius and `a.feeler` (any integer) picks the first feeler side.
 * Writes the urgency (0 = clear) to `a.urgency`.
 */
export function avoidObstacles(eid: number, raycast: RaycastFn, tuning: AiTuning, a: AvoidArgs): void {
  a.urgency = 0
  const ignoreEid = a.ignoreEid
  const radius = a.radius
  const feeler = a.feeler
  loadRot(eid, q)
  const speed = Math.sqrt(Vel.x[eid] * Vel.x[eid] + Vel.y[eid] * Vel.y[eid] + Vel.z[eid] * Vel.z[eid])
  if (speed < MIN_SPEED) {
    rotateVec(q, 0, 0, -1, v)
  } else {
    v.x = Vel.x[eid] / speed
    v.y = Vel.y[eid] / speed
    v.z = Vel.z[eid] / speed
  }
  const length = Math.max(speed * tuning.avoidLookahead, tuning.avoidMinDistance)
  if (!(length > 0)) return
  const ox = Pos64.x[eid]
  const oy = Pos64.y[eid]
  const oz = Pos64.z[eid]
  let found = raycast(setRay(ray, ox, oy, oz, v.x, v.y, v.z, length), eid, hit) && !(ignoreEid >= 0 && hit.eid === ignoreEid)
  if (!found && radius > 0) {
    // Two unit vectors across the flight path.
    const ax = Math.abs(v.y) < 0.9 ? 0 : 1
    const ay = 1 - ax
    // p1 = v × a, p2 = v × p1 (both unit, perpendicular to v).
    let p1x = -v.z * ay
    let p1y = v.z * ax
    let p1z = v.x * ay - v.y * ax
    const l1 = Math.sqrt(p1x * p1x + p1y * p1y + p1z * p1z)
    p1x /= l1
    p1y /= l1
    p1z /= l1
    const p2x = v.y * p1z - v.z * p1y
    const p2y = v.z * p1x - v.x * p1z
    const p2z = v.x * p1y - v.y * p1x
    const off = radius * FEELER_RADII
    for (let k = 0; k < 4 && !found; k++) {
      const side = (feeler + k) & 3
      const c = side === 0 ? 1 : side === 2 ? -1 : 0
      const s = side === 1 ? 1 : side === 3 ? -1 : 0
      n.x = (p1x * c + p2x * s) * off
      n.y = (p1y * c + p2y * s) * off
      n.z = (p1z * c + p2z * s) * off
      found = raycast(setRay(ray, ox + n.x, oy + n.y, oz + n.z, v.x, v.y, v.z, length), eid, hit) && !(ignoreEid >= 0 && hit.eid === ignoreEid)
    }
  }
  if (!found) return

  const urgency = clamp(1 - hit.toi / length, 0, 1)
  inverseRotateVec(q, hit.nx, hit.ny, hit.nz, n)
  let sx = n.x
  let sy = n.y
  const across = Math.sqrt(sx * sx + sy * sy)
  if (across < 1e-3) {
    sx = 0
    sy = 1
  } else {
    sx /= across
    sy /= across
  }
  const keep = 1 - urgency
  const k = tuning.steering.avoidGain * urgency
  Control.pitch[eid] = clamp(Control.pitch[eid] * keep + k * sy, -1, 1)
  Control.yaw[eid] = clamp(Control.yaw[eid] * keep + k * sx, -1, 1)
  // Lateral thrust acts at once while the turn builds; never boost into an obstacle.
  Control.sway[eid] = clamp(Control.sway[eid] * keep + urgency * sx, -1, 1)
  Control.heave[eid] = clamp(Control.heave[eid] * keep + urgency * sy, -1, 1)
  Control.boost[eid] = 0
  a.urgency = urgency
}
